// Growem - Tier 2c: evidence-based STARTING-WEIGHT estimate from the user's basic profile
// (bodyweight + sex + age + training level), for a lift with NO logged history and NO usable
// related/plan-seed proxy. This is the last numeric tier before the honest first-time cue; it exists
// so a brand-new user (or a path-less lift like the barbell RDL / hip thrust) still gets a defensible
// starting point on day one, exactly how Fitbod seeds a first weight from profile + population data,
// but grounded in PUBLISHED strength standards rather than an opaque aggregate.
//
// HONESTY / SAFETY INVARIANTS (do not weaken):
//  1. STARTING_STANDARDS (Tier 2c "sugerido inicial") is FREE WEIGHT ONLY: a %BW barbell standard
//     cannot honestly map to a specific machine's stack (leverage/cam/pulley/sled vary 40%+ by
//     brand). Machines/cables instead get the WEAKER claim of Tier 2d: MACHINE_REFERENCE below, a
//     "referencia" hint from machine-specific population data, always framed with the brand-variance
//     caveat. The two maps MUST stay name-disjoint (unit-tested) so 2d can never bypass 2c's floors.
//  2. CONSERVATIVE. The public "Beginner" tier is the 5th percentile of people who LOG lifts, i.e. an
//     upper bound for a genuine never-trained novice. We size to the TOP of the rep range, cap the
//     bodyweight at a lean (BMI-25) proxy so fat mass does not inflate the number, apply a level-based
//     first-session haircut, round DOWN to the machine step, and never fabricate above the empty bar.
//  3. EDITABLE + LABELLED. The caller renders it dashed as "sugerido inicial", with a start-lighter /
//     leave-2-3-reps-in-reserve line, and the manual override is one tap away.
//  4. NO DATA -> NO NUMBER. If bodyweight is unknown we return null (the caller shows the first-time
//     cue). We never coerce a missing bodyweight into a fabricated target.
//
// COEFFICIENTS: working weight for a set of ~10 reps left at RIR 2 = 0.71 x 1RM (Epley: w = 1RM /
// (1 + (10+2)/30) = 1RM / 1.4). Sex-specific values are read directly off StrengthLevel's Beginner
// bodyweight-ratio tables (they already encode the male/female gap), cross-checked against ExRx
// Untrained where a barbell standard exists. Sources are cited per entry.
//   - RDL:        StrengthLevel romanian-deadlift (M 0.82x/F 0.52x 1RM -> x0.71 working). ExRx DL cross-check.
//   - Hip thrust: StrengthLevel hip-thrust (M 0.72x/F 0.58x 1RM -> x0.71). Assumed BARBELL (see note).
//   - Goblet:     StrengthLevel goblet-squat (M 0.19x/F 0.17x 1RM -> x0.71). Single DB, self-limiting.
//   - DB OHP:     StrengthLevel seated-dumbbell-press, PER HAND (M 0.20x/F 0.10x 1RM -> x0.71).
//   - 1-arm row:  StrengthLevel dumbbell-row, PER HAND (M 0.20x/F 0.15x 1RM -> x0.71).
//   - DB curl:    StrengthLevel dumbbell-curl, PER HAND (M 0.10x/F 0.05x 1RM -> x0.71).
// Sex research (body-mass-normalized): females ~0.75x upper / ~0.85x lower of male %BW; already folded
// into the per-sex StrengthLevel columns, so we pick the column by sex rather than multiplying again.
// Allometric scaling (strength ~ BW^0.67, fat is dead weight): handled by the BMI-25 bodyweight cap,
// the single practical lever, rather than an explicit exponent (which near the anchor is negligible
// and would double-count the cap). Refs: Jaric 2008 (PMID 18172672); NSCA normalizing-fitness-data.

import type { DayState, Sexo, Nivel } from '../types'
import { stepFor } from './rules'

/** A movement pattern label, purely documentary (the coefficients carry the actual numbers). */
type Pattern =
  | 'hip-hinge' | 'hip-thrust' | 'squat-goblet'
  | 'vertical-press-db' | 'horizontal-pull-db' | 'elbow-flexion-db'

/** One free-weight lift's beginner working %BW (per hand for dumbbell single-limb lifts). */
export interface StartStd {
  pattern: Pattern
  coeffM: number      // working %BW, male, 10 reps @ RIR 2
  coeffF: number      // working %BW, female (StrengthLevel already encodes the sex gap)
  perHand?: boolean   // coefficient is per dumbbell/hand (single-limb load)
  barbell?: boolean   // load lives on a barbell: cannot go below the empty 20 kg bar
  source: string      // citation for the audit trail
}

// Keyed by EXACT PLAN exercise name (name = identity, mirrors RELATED_ESTIMATE). Only FREE-WEIGHT
// lifts appear; machines/cables are deliberately absent (invariant #1). For Sebastian's PLAN_V2 the
// only two lifts that actually REACH Tier 2c are 'Peso muerto rumano' and 'Hip thrust' (the rest are
// intercepted by their own plan-seed or a related proxy at a higher tier); the others are listed so
// the engine stays correct for future users / plans that drop those higher-tier signals.
export const STARTING_STANDARDS: Record<string, StartStd> = {
  // Barbell hinge. Free weight -> %BW is honest; coheres with ExRx conventional-DL Untrained (lighter,
  // correct since RDL < conventional). Floor = empty 20 kg bar.
  'Peso muerto rumano': { pattern: 'hip-hinge', coeffM: 0.58, coeffF: 0.37, barbell: true, source: 'StrengthLevel romanian-deadlift; ExRx DL cross-check' },
  // Barbell hip thrust. The PLAN entry does not name the implement; we assume the standard BARBELL
  // hip thrust (its coefficient is barbell-specific and the sibling RDL is barbell). If the user runs
  // it on a machine/Smith the number is still a labelled, editable starting point. Floor = 20 kg bar.
  'Hip thrust': { pattern: 'hip-thrust', coeffM: 0.51, coeffF: 0.41, barbell: true, source: 'StrengthLevel hip-thrust' },
  // Single dumbbell at the chest, self-limiting and low-risk. (Has its own 20 kg plan-seed, so Tier 2c
  // is normally pre-empted; kept for completeness.)
  'Sentadilla Goblet': { pattern: 'squat-goblet', coeffM: 0.13, coeffF: 0.12, source: 'StrengthLevel goblet-squat (M 0.19x/F 0.17x 1RM * 0.71)' },
  // Seated DB shoulder press, per hand. (Normally pre-empted by the logged 'Press militar sentado' proxy.)
  'Press militar con mancuerna neutra': { pattern: 'vertical-press-db', coeffM: 0.14, coeffF: 0.07, perHand: true, source: 'StrengthLevel seated-dumbbell-press (per hand)' },
  // One-arm DB row, per hand. (Normally pre-empted by the 'Remo en polea baja' proxy.)
  'Remo con mancuerna a 1 brazo': { pattern: 'horizontal-pull-db', coeffM: 0.14, coeffF: 0.11, perHand: true, source: 'StrengthLevel dumbbell-row (per hand)' },
  // DB curl, per hand. (Has its own 12.5 kg plan-seed, so normally pre-empted.)
  'Curl con mancuernas': { pattern: 'elbow-flexion-db', coeffM: 0.07, coeffF: 0.036, perHand: true, source: 'StrengthLevel dumbbell-curl (per hand)' },
}

const BARBELL_FLOOR_KG = 20 // empty olympic bar

/** Normalize sex to the two coefficient columns; unknown / 'otro' -> female (the lower, safer column). */
function isMale(sex: Sexo | null | undefined): boolean {
  return sex === 'hombre'
}

/**
 * Age-of-lifter strength factor, anchored to the 18-39 peak = 1.0 and declining after ~40. From the
 * grip-strength normative curve + the "~25% loss by 65 / ~10% per decade" literature, kept conservative
 * (leg power declines a bit faster than grip, so these are if anything slightly generous for the old).
 * Unknown age -> 1.0 (assume adult peak; we never penalize on missing data, only cap on bodyweight).
 * Refs: PMC7749608 (grip normatives), Physiology 2018 (10%/decade), sportsci aging review.
 */
export function ageFactor(age: number | null | undefined): number {
  if (age == null || age < 40) return 1.0
  if (age < 50) return 0.97
  if (age < 60) return 0.90
  if (age < 70) return 0.83
  if (age < 80) return 0.72
  return 0.60
}

/** First-session haircut by training level: a novice's limiter is technique, not strength, and the
 *  Beginner standard is an upper bound, so the newer the lifter the deeper the cut. */
export function levelHaircut(level: Nivel | null | undefined): number {
  if (level === 'avanzado') return 0.95
  if (level === 'intermedio') return 0.90
  return 0.85 // principiante or unknown -> most conservative
}

/**
 * Effective bodyweight for the %BW standard: the LESSER of actual weight and the BMI-25 weight for the
 * user's height (a lean-mass proxy), so fat mass does not inflate a strength estimate (fat adds weight
 * but no force). If height is unknown, fall back to a fixed lean cap (male 90 / female|otro 75 kg).
 */
export function bmiCapKg(heightCm: number | null | undefined, sex: Sexo | null | undefined): number {
  if (heightCm != null && heightCm > 0) {
    const m = heightCm / 100
    return 25 * m * m
  }
  return isMale(sex) ? 90 : 75
}

/** Full years old at `todayIso` (local ISO 'YYYY-MM-DD'), or null if dob is missing/unparseable. */
export function ageFromDob(dob: string | null | undefined, todayIso: string): number | null {
  if (!dob) return null
  const b = dob.slice(0, 10).split('-').map(Number)
  const t = todayIso.slice(0, 10).split('-').map(Number)
  if (b.length !== 3 || t.length !== 3 || b.some(Number.isNaN) || t.some(Number.isNaN)) return null
  let age = t[0] - b[0]
  if (t[1] < b[1] || (t[1] === b[1] && t[2] < b[2])) age-- // birthday not yet reached this year
  return age >= 0 && age < 130 ? age : null
}

/** The minimal profile slice Tier 2c reads (subset of the app Profile). */
export interface EstimateProfile {
  sex: Sexo | null
  heightCm: number | null
  dob: string | null
  level: Nivel | null
}

/**
 * Tier 2c: a conservative starting working weight (kg) for `name` from the user's profile, or null if
 * the lift has no free-weight standard, or bodyweight is unknown, or the honest result would fall below
 * the empty bar (in which case the caller shows the first-time cue instead of forcing 20 kg).
 * `currentBw` is the caller-resolved current bodyweight (latest weigh-in, else onboarding snapshot).
 */
export function profileEstimate(
  name: string,
  profile: EstimateProfile | null | undefined,
  currentBw: number | null | undefined,
  todayIso: string,
): { w: number; perHand: boolean } | null {
  const std = STARTING_STANDARDS[name]
  if (!std || !profile) return null
  if (currentBw == null || currentBw <= 0) return null // invariant #4: no bodyweight -> no number
  // A %BW standard is SEX-SPECIFIC (the male/female gap is ~25-45%). Without a known binary sex we
  // cannot honestly pick a column, and the card would print "segun tu peso" over a silently-guessed
  // number - a false provenance claim. So an unknown / 'otro' sex falls through to the first-time cue
  // rather than defaulting a column. (New users always have sex; this only guards partial/legacy rows.)
  if (profile.sex !== 'hombre' && profile.sex !== 'mujer') return null

  const coeff = isMale(profile.sex) ? std.coeffM : std.coeffF
  const bwEff = Math.min(currentBw, bmiCapKg(profile.heightCm, profile.sex))
  const age = ageFromDob(profile.dob, todayIso)
  const raw = coeff * bwEff * ageFactor(age) * levelHaircut(profile.level)

  const step = stepFor(name)
  const w = Math.floor(raw / step) * step
  if (w <= 0) return null
  // Weak-novice / very-light path: never present the empty bar as the target above an honest sub-20
  // estimate; let the caller fall through to the first-time cue (start with DBs or a light bar).
  if (std.barbell && w < BARBELL_FLOOR_KG) return null
  return { w, perHand: !!std.perHand }
}

// ---------- Tier 2d: machine/cable REFERENCE hints ----------
// Source table + application rules: docs/machine-reference-standards.md (StrengthLevel Beginner kg
// tables read at 80 kg M / 70 kg F, x0.70 for a 10-12 rep working set; audited 2026-07-31).
// coeff model: w = coeff x bwEff(BMI-25 cap) x ageFactor, round DOWN, clamp min 5. NO levelHaircut
// (the Beginner tier already encodes level; stacking would double-discount - audit decision).
// abs model: fixed kg, no body factors, clamp min 5 (2.5 per side). assistance model: MORE kg =
// EASIER -> fraction x ACTUAL bodyweight (no cap: the whole body is hoisted), round UP (over-assist
// errs safe), no level/age factors. Unknown/'otro' sex: FEMALE (lower) column as a labeled
// conservative reference ("never empty" product directive; weaker claim than 2c so this is honest).

export interface MachineRef {
  coeffM?: number; coeffF?: number       // working %BW model
  absM?: number; absF?: number           // fixed-kg model (flat-curve or noisy machines)
  assistM?: number; assistF?: number     // assistance fractions of bodyweight (assisted machines)
  perSide?: boolean
  source: string
}

/** Exercises whose LOAD is assistance (more kg = easier). Progression is inverted everywhere. */
export const ASSISTANCE_EXERCISES: ReadonlySet<string> = new Set(['Dominadas asistidas en máquina'])

export const MACHINE_REFERENCE: Record<string, MachineRef> = {
  'Press de pecho en máquina':                      { coeffM: 0.34, coeffF: 0.12, source: 'strengthlevel chest-press' },
  'Press de hombros en máquina':                    { coeffM: 0.25, coeffF: 0.09, source: 'strengthlevel machine-shoulder-press' },
  'Aperturas en máquina (pec deck)':                { coeffM: 0.37, coeffF: 0.15, source: 'strengthlevel machine-chest-fly' },
  'Cruces en polea':                                { absM: 5, absF: 2.5, perSide: true, source: 'strengthlevel cable-fly (per-hand; absolute floor)' },
  'Jalón al pecho en polea':                        { coeffM: 0.41, coeffF: 0.25, source: 'strengthlevel lat-pulldown' },
  'Jalón al pecho con agarre neutro':               { coeffM: 0.41, coeffF: 0.25, source: 'strengthlevel lat-pulldown (neutral reads >= wide; same ref is conservative)' },
  'Remo sentado en polea':                          { coeffM: 0.41, coeffF: 0.24, source: 'strengthlevel seated-cable-row' },
  'Remo en máquina con apoyo al pecho':             { coeffM: 0.41, coeffF: 0.23, source: 'strengthlevel machine-row' },
  'Remo en máquina con agarre ancho':               { coeffM: 0.41, coeffF: 0.23, source: 'strengthlevel machine-row' },
  'Dominadas asistidas en máquina':                 { assistM: 0.40, assistF: 0.50, source: 'derived from strengthlevel pull-ups (see docs)' },
  'Elevaciones laterales en máquina':               { absM: 15, absF: 5, source: 'strengthlevel machine-lateral-raise (absolute floor)' },
  'Pájaros en máquina':                             { coeffM: 0.25, coeffF: 0.12, source: 'strengthlevel machine-reverse-fly' },
  'Face pull en polea':                             { absM: 10, absF: 7.5, source: 'strengthlevel face-pull (absolute floor)' },
  'Extensión de tríceps en polea alta con cuerda':  { coeffM: 0.19, coeffF: 0.11, source: 'strengthlevel tricep-pushdown' },
  'Extensión de tríceps en polea con cuerda':       { coeffM: 0.19, coeffF: 0.11, source: 'strengthlevel tricep-pushdown' },
  'Extensión de tríceps sobre la cabeza en polea':  { coeffM: 0.12, coeffF: 0.07, source: 'strengthlevel cable-overhead-tricep-extension' },
  'Curl de bíceps en máquina':                      { absM: 17.5, absF: 5, source: 'strengthlevel machine-bicep-curl (flat curve; absolute)' },
  'Prensa de piernas':                              { coeffM: 0.67, coeffF: 0.44, source: 'strengthlevel horizontal-leg-press (lower of the two variants)' },
  'Extensión de piernas en máquina':                { coeffM: 0.42, coeffF: 0.24, source: 'strengthlevel leg-extension' },
  'Curl femoral tumbado en máquina':                { coeffM: 0.26, coeffF: 0.17, source: 'strengthlevel lying-leg-curl' },
  'Curl femoral sentado en máquina':                { coeffM: 0.34, coeffF: 0.21, source: 'strengthlevel seated-leg-curl' },
  'Máquina de abductores':                          { coeffM: 0.33, coeffF: 0.29, source: 'strengthlevel hip-abduction' },
  'Pantorrilla de pie':                             { coeffM: 0.43, coeffF: 0.25, source: 'strengthlevel machine-calf-raise' },
  'Pantorrilla sentado':                            { coeffM: 0.27, coeffF: 0.17, source: 'strengthlevel seated-calf-raise' },
  'Crunch en polea alta':                           { coeffM: 0.23, coeffF: 0.17, source: 'strengthlevel cable-crunch' },
  'Crunch abdominal en máquina':                    { coeffM: 0.25, coeffF: 0.15, source: 'strengthlevel machine-seated-crunch' },
}

/**
 * Tier 2d: a labeled REFERENCE weight for a machine/cable lift. Weaker claim than Tier 2c ("las
 * máquinas varían por marca"). Unknown/'otro' sex uses the female column (conservative). Returns
 * null only when the name has no entry or (coeff model) bodyweight is unknown.
 */
export function machineReference(
  name: string,
  profile: EstimateProfile | null | undefined,
  currentBw: number | null | undefined,
  todayIso: string,
): { w: number; perSide: boolean; assistance: boolean } | null {
  const ref = MACHINE_REFERENCE[name]
  if (!ref) return null
  const male = profile?.sex === 'hombre'
  const step = stepFor(name)

  if (ref.assistM != null) {
    // Assistance: fraction x ACTUAL bodyweight, round UP (more help = safer). Needs bodyweight.
    if (currentBw == null || currentBw <= 0) return null
    const frac = male ? ref.assistM : (ref.assistF ?? ref.assistM)
    const w = Math.ceil((frac * currentBw) / step) * step
    return { w, perSide: false, assistance: true }
  }
  if (ref.absM != null) {
    const w = Math.max(male ? ref.absM : (ref.absF ?? ref.absM), ref.perSide ? 2.5 : 5)
    return { w, perSide: !!ref.perSide, assistance: false }
  }
  // coeff model mirrors the 2c pipeline minus levelHaircut (see docs).
  if (currentBw == null || currentBw <= 0 || !profile) return null
  const coeff = male ? ref.coeffM! : (ref.coeffF ?? ref.coeffM!)
  const bwEff = Math.min(currentBw, bmiCapKg(profile.heightCm, profile.sex))
  const raw = coeff * bwEff * ageFactor(ageFromDob(profile.dob, todayIso))
  const w = Math.max(Math.floor(raw / step) * step, 5)
  return { w, perSide: false, assistance: false }
}

/**
 * Current bodyweight (kg): the MOST RECENT logged weigh-in (day_state.bodyweight_kg), else the
 * onboarding snapshot (profiles.weight_kg), else null. Weigh-ins win because a recomp user's weight
 * drifts and the onboarding value is a one-time snapshot.
 */
export function currentBodyweight(
  days: Record<string, DayState>,
  profileWeightKg: number | null | undefined,
): number | null {
  let latestDay = ''
  let latestW: number | null = null
  for (const dref of Object.values(days)) {
    if (dref.weight != null && dref.day > latestDay) { latestDay = dref.day; latestW = dref.weight }
  }
  if (latestW != null) return latestW
  return profileWeightKg ?? null
}
